import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Globe, Plus, ChevronRight, Lightbulb } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function CreateServer() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggestionData, setSuggestionData] = useState({
    suggested_name: '',
    suggested_icon: '📌',
    parent_category_id: '',
    parent_subcategory_id: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    subsubcategory: '',
    is_private: false
  });

  // Get selected category object
  const selectedCategory = categories.find(c => c.id === formData.category);
  // Get selected subcategory object
  const selectedSubcategory = selectedCategory?.subcategories?.find(s => s.id === formData.subcategory);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    axios.get(`${API}/api/categories`)
      .then(response => setCategories(response.data))
      .catch(error => console.error('Error fetching categories:', error));
  }, [user, navigate]);

  const handleCategoryChange = (value) => {
    setFormData({
      ...formData, 
      category: value, 
      subcategory: '', 
      subsubcategory: ''
    });
  };

  const handleSubcategoryChange = (value) => {
    setFormData({
      ...formData, 
      subcategory: value, 
      subsubcategory: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Build category string (can include subcategory info)
      let categoryValue = formData.category;
      if (formData.subcategory) {
        categoryValue = `${formData.category}/${formData.subcategory}`;
        if (formData.subsubcategory) {
          categoryValue = `${formData.category}/${formData.subcategory}/${formData.subsubcategory}`;
        }
      }

      const serverData = {
        name: formData.name,
        description: formData.description,
        category: categoryValue,
        is_private: formData.is_private
      };

      const response = await axios.post(`${API}/api/servers`, serverData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });

      toast.success('Server created successfully!');
      navigate(`/server/${response.data.server_id}`);
    } catch (error) {
      console.error('Error creating server:', error);
      toast.error('Failed to create server');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestCategory = async () => {
    if (!suggestionData.suggested_name.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/categories/suggest`, suggestionData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Category suggestion submitted! The admin will review it.');
      setShowSuggestionModal(false);
      setSuggestionData({
        suggested_name: '',
        suggested_icon: '📌',
        parent_category_id: '',
        parent_subcategory_id: ''
      });
    } catch (error) {
      console.error('Error suggesting category:', error);
      toast.error('Failed to submit suggestion');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b-4 border-black bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20">
            <Button
              data-testid="back-button"
              onClick={() => navigate(-1)}
              variant="ghost"
              className="mr-4 border-2 border-black rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl md:text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>Create New Server</h1>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border-4 border-black rounded-3xl p-8 md:p-10 shadow-brutal-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-sm font-bold text-slate-900">Server Name *</Label>
              <Input
                id="name"
                data-testid="name-input"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Tennis Players SF"
                className="mt-1 h-14 border-2 border-black rounded-xl font-medium"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-sm font-bold text-slate-900">Description *</Label>
              <Textarea
                id="description"
                data-testid="description-input"
                required
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe your server and what members will find here..."
                rows={4}
                className="mt-1 border-2 border-black rounded-xl font-medium resize-none"
              />
            </div>

            {/* 3-Level Category Selection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-bold text-slate-900">Category *</Label>
                <button
                  type="button"
                  onClick={() => setShowSuggestionModal(true)}
                  className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                >
                  <Lightbulb className="w-3 h-3" />
                  Suggest new category
                </button>
              </div>
              
              {/* Level 1: Main Category */}
              <Select
                value={formData.category}
                onValueChange={handleCategoryChange}
              >
                <SelectTrigger data-testid="category-select" className="h-14 border-2 border-black rounded-xl font-semibold">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <span className="flex items-center gap-2">
                        <span>{cat.icon}</span>
                        <span>{cat.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Level 2: Subcategory */}
              {selectedCategory?.subcategories?.length > 0 && (
                <div className="flex items-center gap-2">
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <Select
                    value={formData.subcategory}
                    onValueChange={handleSubcategoryChange}
                  >
                    <SelectTrigger data-testid="subcategory-select" className="h-12 border-2 border-slate-300 rounded-xl font-medium">
                      <SelectValue placeholder="Select subcategory (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedCategory.subcategories.map(sub => (
                        <SelectItem key={sub.id} value={sub.id}>
                          <span className="flex items-center gap-2">
                            <span>{sub.icon}</span>
                            <span>{sub.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Level 3: Sub-subcategory */}
              {selectedSubcategory?.subcategories?.length > 0 && (
                <div className="flex items-center gap-2 pl-7">
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <Select
                    value={formData.subsubcategory}
                    onValueChange={(value) => setFormData({...formData, subsubcategory: value})}
                  >
                    <SelectTrigger data-testid="subsubcategory-select" className="h-12 border-2 border-slate-200 rounded-xl font-medium">
                      <SelectValue placeholder="Select sub-subcategory (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedSubcategory.subcategories.map(subsub => (
                        <SelectItem key={subsub.id} value={subsub.id}>
                          <span className="flex items-center gap-2">
                            <span>{subsub.icon}</span>
                            <span>{subsub.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Category Breadcrumb Preview */}
              {formData.category && (
                <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-4 py-2 rounded-xl">
                  <span>Selected:</span>
                  <span className="font-medium text-slate-900">
                    {selectedCategory?.name}
                    {formData.subcategory && ` → ${selectedSubcategory?.name}`}
                    {formData.subsubcategory && ` → ${selectedSubcategory?.subcategories?.find(s => s.id === formData.subsubcategory)?.name}`}
                  </span>
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-bold text-slate-900 mb-3 block">Server Type *</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  data-testid="public-option"
                  onClick={() => setFormData({...formData, is_private: false})}
                  className={`border-4 border-black rounded-2xl p-6 cursor-pointer transition-all ${
                    !formData.is_private ? 'bg-lime-100 shadow-brutal' : 'bg-white hover:shadow-brutal'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-lime-400 rounded-full border-2 border-black flex items-center justify-center">
                      <Globe className="w-6 h-6 text-slate-900" />
                    </div>
                    <h3 className="text-xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>Public</h3>
                  </div>
                  <p className="text-sm text-slate-600">Friends can find and join this server</p>
                </div>

                <div
                  data-testid="private-option"
                  onClick={() => setFormData({...formData, is_private: true})}
                  className={`border-4 border-black rounded-2xl p-6 cursor-pointer transition-all ${
                    formData.is_private ? 'bg-pink-100 shadow-brutal' : 'bg-white hover:shadow-brutal'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-pink-400 rounded-full border-2 border-black flex items-center justify-center">
                      <Lock className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>Private</h3>
                  </div>
                  <p className="text-sm text-slate-600">Members can only join via invite</p>
                </div>
              </div>
            </div>

            <Button
              data-testid="submit-button"
              type="submit"
              disabled={loading || !formData.category}
              className="w-full h-14 text-lg font-bold bg-indigo-500 hover:bg-indigo-600 text-white rounded-full shadow-brutal border-2 border-black"
            >
              {loading ? 'Creating...' : 'Create Server'}
            </Button>
          </form>
        </div>
      </div>

      {/* Category Suggestion Modal */}
      {showSuggestionModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-4 border-black rounded-3xl p-6 max-w-md w-full shadow-brutal">
            <h2 className="text-2xl font-black mb-4" style={{fontFamily: 'Outfit'}}>
              💡 Suggest a Category
            </h2>
            <p className="text-sm text-slate-600 mb-4">
              Don&apos;t see the right category? Suggest one and the admin will review it!
            </p>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-bold">Category Name *</Label>
                <Input
                  value={suggestionData.suggested_name}
                  onChange={(e) => setSuggestionData({...suggestionData, suggested_name: e.target.value})}
                  placeholder="e.g., Poker, Cryptocurrency, etc."
                  className="mt-1 border-2 border-black rounded-xl"
                />
              </div>

              <div>
                <Label className="text-sm font-bold">Icon (emoji)</Label>
                <Input
                  value={suggestionData.suggested_icon}
                  onChange={(e) => setSuggestionData({...suggestionData, suggested_icon: e.target.value})}
                  placeholder="📌"
                  className="mt-1 border-2 border-black rounded-xl w-20"
                  maxLength={2}
                />
              </div>

              <div>
                <Label className="text-sm font-bold">Add under category (optional)</Label>
                <Select
                  value={suggestionData.parent_category_id}
                  onValueChange={(value) => setSuggestionData({
                    ...suggestionData, 
                    parent_category_id: value,
                    parent_subcategory_id: ''
                  })}
                >
                  <SelectTrigger className="mt-1 border-2 border-slate-300 rounded-xl">
                    <SelectValue placeholder="As main category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">As main category</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {suggestionData.parent_category_id && suggestionData.parent_category_id !== 'none' && (
                <div>
                  <Label className="text-sm font-bold">Add under subcategory (optional)</Label>
                  <Select
                    value={suggestionData.parent_subcategory_id}
                    onValueChange={(value) => setSuggestionData({...suggestionData, parent_subcategory_id: value})}
                  >
                    <SelectTrigger className="mt-1 border-2 border-slate-200 rounded-xl">
                      <SelectValue placeholder="As subcategory" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">As subcategory</SelectItem>
                      {categories.find(c => c.id === suggestionData.parent_category_id)?.subcategories?.map(sub => (
                        <SelectItem key={sub.id} value={sub.id}>
                          {sub.icon} {sub.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowSuggestionModal(false)}
                className="flex-1 border-2 border-black rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSuggestCategory}
                className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Submit
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
